/* eslint-disable react/no-unescaped-entities */
/**
 * InsightsCategory.jsx — Page catégorie SEO (/insights/categorie/[slug]).
 *
 * Affiche tous les articles publiés d'un cluster (avocat_travail, ec_comptabilite,
 * negociation_impayes, etc.) avec pagination SEO, fil d'Ariane, titre/desc dédiés,
 * JSON-LD CollectionPage.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo, { ldBreadcrumb, ldOrganization } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { ArrowRight, Clock, ChevronLeft, ChevronRight, BookOpen, ArrowLeft } from "lucide-react";

// Cluster metadata — synchronisé avec /app/backend/seo_writer.py
const CLUSTER_META = {
  "avocat_travail":        { label: "Avocat — Droit du travail",     parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "avocat_immobilier":     { label: "Avocat — Droit immobilier",     parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "avocat_societes":       { label: "Avocat — Droit des sociétés",   parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "avocat_fiscal":         { label: "Avocat — Droit fiscal",         parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "avocat_commercial":     { label: "Avocat — Droit commercial",     parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "avocat_recouvrement":   { label: "Avocat — Recouvrement",         parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "avocat_procedure":      { label: "Avocat — Procédure civile",     parent: "Avocats",     cta_route: "/trouver-un-avocat" },
  "ec_comptabilite":       { label: "Expert-comptable — Comptabilité", parent: "Experts-comptables", cta_route: "/trouver-un-expert-comptable" },
  "ec_fiscalite":          { label: "Expert-comptable — Fiscalité",  parent: "Experts-comptables", cta_route: "/trouver-un-expert-comptable" },
  "ec_creation":           { label: "Expert-comptable — Création d'entreprise", parent: "Experts-comptables", cta_route: "/trouver-un-expert-comptable" },
  "ec_transmission":       { label: "Expert-comptable — Transmission", parent: "Experts-comptables", cta_route: "/trouver-un-expert-comptable" },
  "ec_gestion":            { label: "Expert-comptable — Gestion",    parent: "Experts-comptables", cta_route: "/trouver-un-expert-comptable" },
  "cdj_recouvrement":      { label: "Commissaire de justice — Recouvrement", parent: "Commissaires de justice", cta_route: "/trouver-un-commissaire-de-justice" },
  "cdj_constats":          { label: "Commissaire de justice — Constats",     parent: "Commissaires de justice", cta_route: "/trouver-un-commissaire-de-justice" },
  "cdj_execution":         { label: "Commissaire de justice — Exécution",    parent: "Commissaires de justice", cta_route: "/trouver-un-commissaire-de-justice" },
  "negociation_impayes":   { label: "Négociation — Factures impayées",       parent: "Négociation", cta_route: "/negociation" },
  "negociation_delais":    { label: "Négociation — Délais de paiement",      parent: "Négociation", cta_route: "/negociation" },
  "negociation_litiges":   { label: "Négociation — Litiges commerciaux",     parent: "Négociation", cta_route: "/negociation" },
  "negociation_amiable":   { label: "Négociation — Solutions amiables",      parent: "Négociation", cta_route: "/negociation" },
  "entreprises":           { label: "Entreprises — Gestion des litiges",     parent: "Thématiques", cta_route: "/trouver-un-avocat" },
  "patrimoine":            { label: "Patrimoine — Conseil & transmission",   parent: "Thématiques", cta_route: "/trouver-un-notaire" },
  "immobilier":            { label: "Immobilier — Transaction & litiges",    parent: "Thématiques", cta_route: "/trouver-un-notaire" },
  "contentieux":           { label: "Contentieux — Gestion",                 parent: "Thématiques", cta_route: "/trouver-un-avocat" },
};

const PAGE_SIZE = 12;

export default function InsightsCategory() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
  const skip = (page - 1) * PAGE_SIZE;
  const meta = CLUSTER_META[slug];

  useEffect(() => {
    if (!meta) { setLoading(false); return; }
    setLoading(true);
    apiClient.get("/insights", { params: { category: slug, limit: PAGE_SIZE, skip } })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [slug, skip, meta]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Navbar variant="light" />
        <main className="max-w-md mx-auto px-6 py-20 text-center">
          <h1 className="font-serif text-2xl text-[#0B3A2A] mb-3">Catégorie inconnue</h1>
          <p className="text-sm text-slate-600 mb-6">La catégorie « {slug} » n'existe pas.</p>
          <Button asChild><Link to="/insights">Retour aux Insights</Link></Button>
        </main>
        <Footer />
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `/insights/categorie/${slug}${page > 1 ? `?page=${page}` : ""}`;

  return (
    <div className="bg-[#FAFAF7] min-h-screen" data-testid="insights-category">
      <Seo
        title={`${meta.label} — Articles & analyses CONFILEX`}
        description={`Articles, analyses et guides institutionnels CONFILEX sur le thème ${meta.label.toLowerCase()}. ${total} article${total > 1 ? "s" : ""} ${total > 1 ? "publiés" : "publié"}.`}
        canonical={canonical}
        keywords={[meta.label.toLowerCase(), meta.parent.toLowerCase(),
                   "articles juridiques", "guides CONFILEX", "analyses sectorielles"]}
        jsonLd={[
          ldOrganization(),
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: "Insights", url: "/insights" },
            { name: meta.parent, url: `/insights?category=${slug}` },
            { name: meta.label, url: canonical },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${meta.label} — CONFILEX`,
            "url": `${baseUrl}${canonical}`,
            "isPartOf": { "@type": "WebSite", "name": "CONFILEX", "url": baseUrl || "https://confilex.fr" },
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": total,
              "itemListElement": items.slice(0, 10).map((a, i) => ({
                "@type": "ListItem", "position": i + 1,
                "url": `${baseUrl}/insights/${a.slug}`,
                "name": a.title,
              })),
            },
          },
        ]}
      />
      <Navbar variant="light" />

      {/* HEADER */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/insights" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B3A2A] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Tous les Insights
        </Link>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-2">
          <BookOpen className="inline w-3.5 h-3.5 mr-1" /> {meta.parent}
        </p>
        <h1 className="font-serif text-4xl lg:text-5xl text-[#0B3A2A] tracking-tight mb-3" data-testid="category-title">
          {meta.label}
        </h1>
        <p className="text-sm text-slate-600 max-w-2xl mb-6">
          {total} article{total > 1 ? "s" : ""} {total > 1 ? "publiés" : "publié"} sur ce thème.
          Articles d'analyse institutionnelle — aucune consultation juridique personnalisée.
        </p>
        <Button asChild className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="cta-find-expert">
          <Link to={meta.cta_route}>Trouver un professionnel adapté <ArrowRight className="w-4 h-4 ml-1" /></Link>
        </Button>
      </section>

      {/* GRID */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-12">
        {loading ? (
          <p className="text-slate-500 text-sm text-center py-12">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
            <p className="text-sm text-slate-600">
              Aucun article publié pour cette catégorie pour le moment.
            </p>
            <Button asChild variant="outline" size="sm" className="mt-4">
              <Link to="/insights">Découvrir les autres Insights</Link>
            </Button>
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="articles-grid">
            {items.map(a => <ArticleCard key={a.id} article={a} />)}
          </ul>
        )}

        {/* Pagination SEO */}
        {totalPages > 1 && (
          <nav className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setSearchParams({ page: String(page - 1) })}>
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Précédent
            </Button>
            <span className="text-xs text-slate-500 mx-3">
              Page <b className="text-[#0B3A2A]">{page}</b> / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages}
              onClick={() => setSearchParams({ page: String(page + 1) })}>
              Suivant <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </nav>
        )}
      </section>

      <Footer />
    </div>
  );
}

function ArticleCard({ article: a }) {
  return (
    <li className="group">
      <Link to={`/insights/${a.slug}`}
        className="block rounded-xl bg-white border border-slate-200 p-5 hover:border-[#C9A961] transition h-full">
        <p className="text-[10px] uppercase tracking-wider text-[#C9A961] mb-2 font-medium">
          {(a.profession_tags || [])[0] || "Article"}
        </p>
        <h3 className="font-serif text-lg text-[#0B3A2A] leading-snug line-clamp-3 mb-2 group-hover:text-[#C9A961] transition">
          {a.title}
        </h3>
        {a.excerpt && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{a.excerpt}</p>}
        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-auto">
          {a.reading_time && (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.reading_time} min</span>
          )}
          {a.published_at && <span>{a.published_at.slice(0, 10)}</span>}
        </div>
      </Link>
    </li>
  );
}
